// The ops console's pages (D17, D18): the approval queue, the case list, one case with its
// conversation, and the WhatsApp flow's settings for tenant admins.
import { useCallback, useEffect, useState } from "react";

import { ApprovalCard } from "./ApprovalCard";
import { api, openDocument, send } from "./api";
import { ImportSheet, MemberSettings, PlansAdmin, WritersAdmin } from "./MemberSettings";
import { TemplatePicker } from "./Templates";
import type { Approval, CasePage, CaseRow } from "./types";

// Load something, and again every `every` ms; `reload` forces it.
export function usePoll<T>(load: () => Promise<T>, every: number): { data: T | null; error: string; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((t) => t + 1), []);
  useEffect(() => {
    let live = true;
    load()
      .then((d) => live && (setData(d), setError("")))
      .catch((e) => live && setError(String((e as Error).message)));
    const timer = window.setInterval(() => setTick((t) => t + 1), every);
    return () => {
      live = false;
      window.clearInterval(timer);
    };
  }, [tick]); // `load` is a new function each render; the tick decides when to load again
  return { data, error, reload };
}

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "");

export function Queue({ onOpen }: { onOpen: (caseId: string) => void }) {
  const { data, error, reload } = usePoll(() => api<Approval[]>("/api/approvals"), 5000);
  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  if (data.length === 0) {
    return <p className="muted">Nothing waits for you.</p>;
  }
  return (
    <div className="stack">
      {data.map((a) => (
        <div key={a.id}>
          <ApprovalCard approval={a} onDone={reload} />
          <button className="link" onClick={() => onOpen(a.case_id)}>
            Open the case
          </button>
        </div>
      ))}
    </div>
  );
}

const STATUS: Record<string, string> = {
  pending: "Waiting to be allowed",
  active: "Running",
  escalated: "With ops",
  done: "Done",
  closed: "Closed",
};

export function Cases({ onOpen }: { onOpen: (caseId: string) => void }) {
  const { data, error } = usePoll(() => api<CaseRow[]>("/api/cases"), 10000);
  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  if (data.length === 0) {
    return <p className="muted">No cases yet.</p>;
  }
  return (
    <table className="list">
      <thead>
        <tr>
          <th>Who</th>
          <th>Status</th>
          <th>Step</th>
          <th>Waiting</th>
          <th>Last message</th>
        </tr>
      </thead>
      <tbody>
        {data.map((c) => (
          <tr key={c.id} onClick={() => onOpen(c.id)}>
            <td>
              {c.emergencies > 0 && <span className="badge warn">Emergency</span>} {c.display_name || "Unknown"}
            </td>
            <td>
              {STATUS[c.status] ?? c.status}
              {c.mode === "ops" && c.status === "active" ? " (ops hold it)" : ""}
            </td>
            <td>{c.step ?? ""}</td>
            <td>{c.waiting > 0 ? <span className="badge">{c.waiting}</span> : ""}</td>
            <td>{when(c.last_message_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Case({ id }: { id: string }) {
  const { data, error, reload } = usePoll(() => api<CasePage>(`/api/cases/${id}`), 5000);
  const [text, setText] = useState("");
  const [step, setStep] = useState("");
  const [contact, setContact] = useState({ name: "", phone: "" });
  const [problem, setProblem] = useState("");

  const act = async (path: string, body: unknown = {}, method = "POST") => {
    setProblem("");
    try {
      await send(path, body, method);
      reload();
      return true;
    } catch (e) {
      setProblem(String((e as Error).message));
      return false;
    }
  };

  if (error) {
    return <p className="note warn">{error}</p>;
  }
  if (!data) {
    return <p className="muted">Loading…</p>;
  }
  const c = data.case;
  const running = c.status === "active" || c.status === "escalated";
  const waiting = data.approvals.filter((a) => a.status === "waiting");
  return (
    <div className="case">
      <div className="case-head">
        <h2>
          {data.conversation.display_name || "Unknown"} <span className="muted">{data.conversation.masked}</span>
        </h2>
        <p>
          {STATUS[c.status] ?? c.status} · {c.sop_id} v{c.sop_version} · step {c.step ?? "none yet"} ·{" "}
          {data.conversation.mode === "ops" ? "ops hold the thread" : "the agent drafts"}
        </p>
        <div className="actions">
          {c.status === "pending" && <button onClick={() => act(`/api/cases/${id}/allow`)}>Allow this sender</button>}
          {running && data.conversation.mode === "agent" && <button onClick={() => act(`/api/cases/${id}/take-over`)}>Take over</button>}
          {running && data.conversation.mode === "ops" && (
            <>
              <select value={step} onChange={(e) => setStep(e.target.value)}>
                <option value="">Hand back where it was</option>
                {data.sop.steps.map((s) => (
                  <option key={s.id} value={s.id}>
                    Hand back at: {s.title}
                  </option>
                ))}
              </select>
              <button onClick={() => act(`/api/cases/${id}/hand-back`, { step: step || null })}>Hand back</button>
            </>
          )}
          {c.status !== "closed" && (
            <button className="secondary" onClick={() => window.confirm("Close this case?") && act(`/api/cases/${id}/close`)}>
              Close
            </button>
          )}
        </div>
        {problem && <p className="note warn">{problem}</p>}
      </div>

      <div className="case-body">
        <section className="thread">
          {data.emergencies
            .filter((e) => !e.acknowledged_at)
            .map((e) => (
              <div key={e.id} className="banner">
                Emergency ({e.trigger}): reply {e.reply_status}; bridge{" "}
                {e.bridge.call ? `rang ${e.bridge.to}` : e.bridge.skipped || e.bridge.error || "pending"}.{" "}
                <button onClick={() => act(`/api/emergencies/${e.id}/acknowledge`)}>Acknowledge</button>
              </div>
            ))}
          {data.messages.map((m) => (
            <div key={m.id} className={`bubble ${m.direction}`}>
              <div className="meta">
                {m.direction === "in" ? "Member" : m.author === "agent" ? "Agent (approved)" : m.author === "ops" ? "Ops" : "Emergency reply"} ·{" "}
                {when(m.created_at)}
                {m.direction === "out" && m.status !== "sent" ? ` · ${m.status}` : ""}
              </div>
              {m.document_id && (
                <button className="link" disabled={m.document_status !== "stored"} onClick={() => openDocument(m.document_id!, m.document_name ?? "")}>
                  {m.kind}: {m.document_name || "file"} {m.document_status !== "stored" ? `(${m.document_status})` : "(view)"}
                </button>
              )}
              {m.text && <div className="text">{m.text}</div>}
              {m.error && <div className="note warn">{m.error}</div>}
            </div>
          ))}
          {waiting.map((a) => (
            <ApprovalCard
              key={a.id}
              approval={{ ...a, display_name: data.conversation.display_name, window_open: data.conversation.window_open }}
              onDone={reload}
            />
          ))}
          {data.conversation.mode === "ops" && data.conversation.window_open && (
            <div className="compose">
              <textarea rows={3} placeholder="Write to the member yourself" value={text} onChange={(e) => setText(e.target.value)} />
              <button
                disabled={!text.trim()}
                onClick={async () => (await act(`/api/conversations/${c.conversation_id}/messages`, { text })) && setText("")}
              >
                Send
              </button>
            </div>
          )}
          {data.conversation.mode === "ops" && !data.conversation.window_open && (
            <div className="compose">
              <p className="note">More than 24 hours since the member's last message: only an approved template can go.</p>
              <TemplatePicker
                member={data.conversation.display_name}
                busy={false}
                onSend={(template) => act(`/api/conversations/${c.conversation_id}/messages`, { template })}
              />
            </div>
          )}
        </section>

        <aside className="side">
          <Fields data={data} act={act} />
          {c.summary && <p className="note">{c.summary}</p>}
          <h3>Documents</h3>
          {data.documents.length === 0 && <p className="muted">None yet.</p>}
          {data.documents.map((d) => (
            <div key={d.id} className="document">
              <button className="link" disabled={d.status !== "stored"} onClick={() => openDocument(d.id, d.filename)}>
                {d.filename || d.kind}
              </button>{" "}
              <span className="muted">
                {d.status}
                {d.reading?.document_type ? ` · ${d.reading.document_type.replace(/_/g, " ")}` : ""}
                {d.reading && d.reading.readable === false ? " · not readable" : ""}
              </span>
              {d.reading?.notes && <p className="note">{d.reading.notes}</p>}
              {d.reading?.problem && <p className="note warn">{d.reading.problem}</p>}
            </div>
          ))}
          <h3>Parent at home</h3>
          <p className="muted">Rung by the emergency path when the member writes from abroad.</p>
          {data.contacts.map((p) => (
            <p key={p.id}>
              {p.name || "Unnamed"} +{p.phone} ({p.relation}){" "}
              <button className="link" onClick={() => act(`/api/contacts/${p.id}`, {}, "DELETE")}>
                remove
              </button>
            </p>
          ))}
          <input placeholder="Name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
          <input placeholder="Phone in India" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
          <button
            disabled={!contact.phone.trim()}
            onClick={async () =>
              (await act(`/api/households/${c.household_id}/contacts`, { ...contact, relation: "parent", at_home: true })) &&
              setContact({ name: "", phone: "" })
            }
          >
            Add parent
          </button>
        </aside>
      </div>
    </div>
  );
}

const LABELS: Record<string, string> = {
  full_name: "Name",
  date_of_birth: "Date of birth",
  uan: "UAN",
  member_id: "Member ID",
  date_of_joining: "Joined",
  date_of_exit: "Left",
  account_last4: "Bank account (last 4)",
  last_employer: "Last employer",
  country: "Country",
};
const label = (k: string) => LABELS[k] ?? k.replace(/_/g, " ");

// The case's details: what ops confirmed, what's unconfirmed, and what documents suggest (decision 15).
// A suggestion goes into the case only when ops confirm it, and never over a confirmed field.
function Fields({ data, act }: { data: CasePage; act: (path: string, body?: unknown) => Promise<boolean> }) {
  const [editing, setEditing] = useState<{ name: string; value: string } | null>(null);
  const c = data.case;
  const url = (name: string) => `/api/cases/${c.id}/fields/${name}`;
  const docName = (id?: string) => data.documents.find((d) => d.id === id)?.filename || "a document";
  const save = async () => {
    if (editing && (await act(url(editing.name), { action: "confirm", value: editing.value }))) {
      setEditing(null);
    }
  };
  const editor = (name: string) =>
    editing?.name === name ? (
      <span className="edit">
        <input value={editing.value} onChange={(e) => setEditing({ name, value: e.target.value })} />
        <button disabled={!editing.value.trim()} onClick={save}>
          Save
        </button>
        <button className="secondary" onClick={() => setEditing(null)}>
          Cancel
        </button>
      </span>
    ) : null;
  const names = Object.keys(c.fields);
  const suggested = Object.entries(c.suggestions ?? {});
  return (
    <>
      <h3>Details</h3>
      {names.length === 0 && <p className="muted">None collected yet.</p>}
      <dl>
        {names.map((k) => {
          const v = c.fields[k];
          const shown = Array.isArray(v) ? v.join(", ") : String(v);
          return (
            <div key={k}>
              <dt>{label(k)}</dt>
              <dd>
                {editor(k) ?? (
                  <>
                    {shown} {c.confirmed?.[k] ? <span className="badge ok">confirmed</span> : <span className="muted">(unconfirmed)</span>}{" "}
                    <button className="link" onClick={() => setEditing({ name: k, value: shown })}>
                      {c.confirmed?.[k] ? "correct" : "confirm"}
                    </button>
                  </>
                )}
              </dd>
            </div>
          );
        })}
      </dl>
      {suggested.length > 0 && (
        <>
          <h3>Read from documents</h3>
          <p className="muted">Gemini's reading: confirm what's right, correct or dismiss the rest.</p>
          <dl>
            {suggested.map(([k, s]) => (
              <div key={k}>
                <dt>{label(k)}</dt>
                <dd>
                  {editor(k) ?? (
                    <>
                      {s.value} <span className="muted">from {docName(s.document_id)}</span>{" "}
                      {c.fields[k] !== undefined && String(c.fields[k]) !== s.value && <span className="badge warn">differs</span>}
                      <span className="actions">
                        <button onClick={() => act(url(k), { action: "confirm" })}>Confirm</button>
                        <button className="secondary" onClick={() => setEditing({ name: k, value: s.value })}>
                          Correct
                        </button>
                        <button className="secondary" onClick={() => act(url(k), { action: "dismiss" })}>
                          Dismiss
                        </button>
                      </span>
                    </>
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </>
      )}
    </>
  );
}

type Sop = { id: string; version: number; title: string };

const lines = (value: unknown) => (Array.isArray(value) ? value.join("\n") : "");
const split = (value: string) =>
  value
    .split(/[\n,]/)
    .map((v) => v.trim())
    .filter(Boolean);

export function Settings() {
  const [settings, setSettings] = useState<Record<string, Record<string, unknown>> | null>(null);
  const [sops, setSops] = useState<Sop[]>([]);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    api<Record<string, Record<string, unknown>>>("/api/settings").then(setSettings).catch((e) => setSaved(String(e.message)));
    api<Sop[]>("/api/sops").then(setSops).catch(() => setSops([]));
  }, []);

  if (!settings) {
    return <p className="muted">{saved || "Loading…"}</p>;
  }
  const whatsapp = settings.whatsapp ?? {};
  const emergency = settings.emergency ?? {};
  const set = (group: "whatsapp" | "emergency", key: string, value: unknown) =>
    setSettings({ ...settings, [group]: { ...(settings[group] ?? {}), [key]: value } });

  return (
    <>
      <div className="settings">
        <h2>WhatsApp</h2>
        <label>
          SOP for new conversations
          <select value={String(whatsapp.sop ?? "")} onChange={(e) => set("whatsapp", "sop", e.target.value)}>
            <option value="">None: ops reply by hand</option>
            {sops.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title} (v{s.version})
              </option>
            ))}
          </select>
        </label>
        <label className="check">
          <input type="checkbox" checked={Boolean(whatsapp.allow_all)} onChange={(e) => set("whatsapp", "allow_all", e.target.checked)} />
          Allow every sender (otherwise only the numbers below, and those ops allow)
        </label>
        <label>
          Allow-list: one number a line, with the country code
          <textarea rows={4} value={lines(whatsapp.allow_list)} onChange={(e) => set("whatsapp", "allow_list", split(e.target.value))} />
        </label>

        <h2>WhatsApp templates</h2>
        <p className="muted">
          For messages more than 24 hours after the member's last one. Tick a template once Meta has approved it in WATI; only ticked ones are offered.
        </p>
        {Object.entries((whatsapp.template_texts as Record<string, string>) ?? {}).map(([name, body]) => {
          const approved = ((whatsapp.templates as string[]) ?? []).includes(name);
          return (
            <div key={name} className="template-setting">
              <label className="check">
                <input
                  type="checkbox"
                  checked={approved}
                  onChange={(e) => {
                    const list = ((whatsapp.templates as string[]) ?? []).filter((n) => n !== name);
                    set("whatsapp", "templates", e.target.checked ? [...list, name] : list);
                  }}
                />
                <strong>{name}</strong> approved by Meta
              </label>
              <textarea
                rows={2}
                value={body}
                onChange={(e) => set("whatsapp", "template_texts", { ...((whatsapp.template_texts as Record<string, string>) ?? {}), [name]: e.target.value })}
              />
            </div>
          );
        })}

        <h2>Emergency path</h2>
        <label>
          Fixed reply when someone is rung (sent at once, without an approval)
          <textarea rows={2} value={String(emergency.reply ?? "")} onChange={(e) => set("emergency", "reply", e.target.value)} />
        </label>
        <label>
          Fixed reply when nobody can be rung (a lead abroad with no parent on file)
          <textarea rows={2} value={String(emergency.reply_no_call ?? "")} onChange={(e) => set("emergency", "reply_no_call", e.target.value)} />
        </label>
        <label>
          Duty phones: one a line
          <textarea rows={3} value={lines(emergency.duty_phones)} onChange={(e) => set("emergency", "duty_phones", split(e.target.value))} />
        </label>
        <label>
          Exotel flow that alerts a duty phone (App ID; none set, no duty call)
          <input value={String(emergency.alert_app_id ?? "")} onChange={(e) => set("emergency", "alert_app_id", e.target.value.trim())} />
        </label>
        <label>
          Niro Emergency's ExoPhone
          <input value={String(emergency.exophone ?? "")} onChange={(e) => set("emergency", "exophone", e.target.value.trim())} />
        </label>
        <label>
          Live flow the bridge connects into (App ID)
          <input value={String(emergency.flow_app_id ?? "")} onChange={(e) => set("emergency", "flow_app_id", e.target.value.trim())} />
        </label>
        <MemberSettings settings={settings} onChange={setSettings} />
        <div className="actions">
          <button
            onClick={() =>
              send("/api/settings", settings, "PUT")
                .then(() => setSaved("Saved."))
                .catch((e) => setSaved(String(e.message)))
            }
          >
            Save
          </button>
          {saved && <span className="muted">{saved}</span>}
        </div>
      </div>
      <PlansAdmin />
      <WritersAdmin />
      <ImportSheet />
    </>
  );
}
